import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {PerformanceService} from "../../../../performance/performance.service";
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {ActivityAction, IActivity} from "../../../../../../activities/activity";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../../store";
import {Subject} from "rxjs";
import {TypeUtils} from "../../../../../../shared/utils/type-utils";
import {Product} from "../../../../product";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import {Website} from "../../../../website";
import {ActivatedRoute, Router} from "@angular/router";
import {UserSubscription, UserSubscriptionIds} from '../../../../../../user/user-subscription';
import {DbResponse} from "../../../../../../shared/interceptors/db-response";
import {WebsiteMapper} from "../../../../website-mapper";
import {User} from "../../../../../../user/user";
import {DbModalService} from "../../../../../../modules/modal/modal";
import {Utils} from "../../../../../../shared/utils/utils";
import {SignUpFor, TypeFormAnswers} from "../../../../../../add-website/typeFormAnswers";

@Component({
    selector: 'db-booster-page-score',
    templateUrl: './booster-page-score.component.html',
    styleUrls: ['./booster-page-score.component.scss'],
    standalone: false
})
export class BoosterPageScoreComponent implements OnInit, OnDestroy, OnChanges {

  @Input() companyName: any;
  @Input() websiteId: number;
  @Input() websiteUrl: string;
  @Input() websiteHosted: boolean;
  @Input() trialBoosterWithHosted: boolean;
  @Input() userSubscription: UserSubscription;
  @Input() optimizerPlugin: Product;
  @Input() websiteScore: any;
  @Input() currentScoreDate: Date | string;

  public userSubscriptionIds = UserSubscriptionIds;
  public analyzingProgress: number = 0;
  public analyzingUndefined: number = 0;
  public analyzing: boolean = false;
  public analyzingFinished: boolean = false;
  public analyzingAfterActivities: number = 0;
  public optimizaionId: any = null;
  public analyzingBefore: boolean;
  public loading: boolean = false;
  public timezone: string;
  public website: Website;
  public currentDesktopScore: number;
  public currentDesktopTTI: number;
  public currentMobileScore: number;
  public currentMobileTTI: number;
  public currentLastOptimizedDate: Date | string;
  public originalScore: any;
  public originalDesktopScore: number;
  public originalDesktopTTI: number;
  public originalMobileScore: number;
  public originalMobileTTI: number;
  public originalLastOptimizedDate: Date | string;
  public showImproveBadge: boolean;
  public improvedPercent: number;
  public significantDesktopScoreChange;
  public significantMobileScoreChange;
  public user: User;
  public signUpForAnswers: SignUpFor;
  public typeFormAnswers = TypeFormAnswers;
  public demoUser: boolean = false;
  public boosterPluginIsActive: boolean = false;
  private _simulationTimer: any;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _performanceService: PerformanceService,
    private _modalService: DbModalService,
    private _store: Store<fromStore.DbStoreState>,
    private _cdRef: ChangeDetectorRef,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _route: ActivatedRoute,
  ) {
  }

  ngOnInit() {
    const queryParams: any = (<any>this._route.queryParams).value;
    this.analyzing = Utils.isTruthy(queryParams.fromExploreDialog) || false;
    this.getTimeZone();
    this._addEventHandlers();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['websiteScore'] && changes['websiteScore'].currentValue) {
      this.websiteScore = changes['websiteScore'].currentValue;
      this._getScoreInfo(this.websiteScore && this.websiteScore.currentScore, this.websiteScore && this.websiteScore.originalScore);
    }
  }

  private _getScoreInfo(currentScore, originalScore): void {
    if (currentScore) {
      this.currentDesktopScore = currentScore.desktopScore;
      this.currentDesktopTTI = currentScore.desktopTTI;
      this.currentMobileScore = currentScore.mobileScore;
      this.currentMobileTTI = currentScore.mobileTTI;
    }
    this.currentLastOptimizedDate = (currentScore && currentScore.date) || this.currentScoreDate;
    if (originalScore) {
      this.originalDesktopScore = originalScore.desktopScore;
      this.originalDesktopTTI = originalScore.desktopTTI;
      this.originalMobileScore = originalScore.mobileScore;
      this.originalMobileTTI = originalScore.mobileTTI;
    }
    if (originalScore && currentScore) {
      const desktopScoreImprove = this.currentDesktopScore && this.originalDesktopScore && (((this.currentDesktopScore - this.originalDesktopScore) / this.originalDesktopScore) * 100);
      const mobileScoreImprove = this.currentMobileScore && this.originalMobileScore && (((this.currentMobileScore - this.originalMobileScore) / this.originalMobileScore) * 100);
      const maxScore = desktopScoreImprove && mobileScoreImprove && Math.max(desktopScoreImprove, mobileScoreImprove);
      this.showImproveBadge = maxScore && Math.round(maxScore) > 20;
      this.improvedPercent = maxScore && Math.round(maxScore) > 20 && Math.round(maxScore);
    }
    if (this.currentDesktopScore && this.currentMobileScore && this.originalDesktopScore && this.originalMobileScore) {
      this.significantDesktopScoreChange = ((Math.log((150 - this.originalDesktopScore) / (150 - this.currentDesktopScore)) / Math.log((150 - (this.originalDesktopScore)) / 50)) < 0.45) && (this.currentDesktopScore < 90); // 0.45 coefficient
      this.significantMobileScoreChange = ((Math.log((150 - this.originalMobileScore) / (150 - this.currentMobileScore)) / Math.log((150 - this.originalMobileScore) / 50)) < 0.45) && (this.currentMobileScore < 90); // 0.45 coefficient
    }
  }

  private _getConnectionFlowStep(): void {
    !!this.websiteId && this._performanceService.getConnectionFlowProgress(this.websiteId)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first()
      )
      .subscribe((response: DbResponse): void => {
        if (response.success) {

          const originalScore = response.data && response.data.debugData && response.data.debugData.not_optimized_score_check_finish ? response.data.debugData.not_optimized_score_check_finish : null;
          this.websiteScore.originalScore = !TypeUtils.isNull(originalScore) && WebsiteMapper.mapScoreData(originalScore);
          this.originalScore = this.websiteScore.originalScore;
          this.originalLastOptimizedDate = response.data && new Date(response.data.createdAt);
          this.optimizaionId = response.data && response.data.id ? response.data.id : null;
          if (!TypeUtils.isNull(this.optimizaionId)) {
            this._getBeforeAndAfterOptimizationScores(this.optimizaionId);
          } else {
            if (!this.websiteScore.currentScore || this.websiteScore.currentScore.desktopScore === 101) {
              this.analyzing = true;
              this.analyzingProgress = 0;
              this._performanceService.getGooglePageSpeedScore(this.websiteId);
            }
          }
        }
      });
  }

  private _getBeforeAndAfterOptimizationScores(optimizationId) {
    const scoreOld = this.website && this.website.score && this.website.score.originalScore && this.website.score.originalScore.desktopScore ? this.website.score.originalScore.desktopScore : null;
    const scoreNew = this.website && this.website.score && this.website.score.currentScore && this.website.score.currentScore.desktopScore ? this.website.score.currentScore.desktopScore : null;
    if (TypeUtils.isNull(scoreNew) || scoreNew === 101) {
      this.analyzing = true;
      this._performanceService.postOptimizationTasks(this.websiteId, optimizationId)
        .pipe(filter((response: DbResponse): boolean => !!response),
        first())
        .subscribe((response: DbResponse): void => {
          if (TypeUtils.isNull(scoreOld) || scoreOld === 101) {
            (!this.analyzing || !!this.analyzingFinished) && this._setNoOptimizeScore(optimizationId);
          }
        });
    } else if (TypeUtils.isNull(scoreOld) || scoreOld === 101) {
      (!this.analyzing || !!this.analyzingFinished) && this._setNoOptimizeScore(optimizationId);
    }
    return true;
  }

  private _setNoOptimizeScore(optimizationId): void {
    this.analyzingBefore = true;
    this._performanceService.setNotOptimizedScore(this.websiteId, optimizationId)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        setTimeout(() => this._store.dispatch(new fromStore.LoadWebsites()), 1500);
        setTimeout(() => this.analyzingBefore = false, 2500);

      });
  }


  public getTimeZone(): void {
    const getTimeZoneOffset = new Date().getTimezoneOffset() / 60;
    const getTimeZone = getTimeZoneOffset < 0 ? Math.abs(getTimeZoneOffset) : getTimeZoneOffset * (-1);
    this.timezone = getTimeZone.toString();
  }

  ngOnDestroy() {
    clearInterval(this._simulationTimer);
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _addEventHandlers(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUser)),
        first()
      )
      .subscribe((user: User) => {
        this.user = user;
        this.signUpForAnswers = user && user.signUpFor;
        this.demoUser = Utils.isDemoUser(this.userSubscription, this.user);
      });

    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.websiteId))
      .subscribe((optimizerPlugin): void => {
        if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
          this.optimizerPlugin = optimizerPlugin;
          this._cdRef.markForCheck();
          this.boosterPluginIsActive = optimizerPlugin.active;
        }
      });

    this._store
      .select(fromStore.getGoogleScoreOngoingActivity(this.websiteId))
      .pipe(takeUntil(this._destroyStream$))
      .subscribe((activity: IActivity) => {
        if (activity && activity.progress >= 0
          && activity.meta
          && (
            (!TypeUtils.isUndefined(activity.meta.is_first_optimization_flow) && (!!activity.meta.is_first_optimization_flow || TypeUtils.isNull(activity.meta.is_first_optimization_flow)))
            ||
            (!TypeUtils.isUndefined(activity.meta.check_with_no_optimization) && !activity.meta.check_with_no_optimization)
          )
        ) {
          if (activity.progress === 100) {
            this.analyzingFinished = true;
            setTimeout(() => this.analyzing = false, 2000);
          } else {
            this.analyzing = activity.progress !== 100;
          }
          this.analyzingAfterActivities++;

          if ((!this.analyzing || !!this.analyzingFinished) && !TypeUtils.isNull(this.optimizaionId)) {
            const scoreOld = this.website && this.website.score && this.website.score.originalScore && this.website.score.originalScore.desktopScore && this.website.score.originalScore.desktopScore !== 101 ? this.website.score.originalScore.desktopScore : null;
            if (TypeUtils.isNull(scoreOld) || scoreOld === 101) {
              this._setNoOptimizeScore(this.optimizaionId);
            }
          }
        }
        this.analyzingAfterActivities > 0 && this.analyzing && this._defineAnalyzing(activity);

        if (!this.analyzing && this.analyzingProgress < 1) {
          this.analyzingProgress = 0;
          clearInterval(this._simulationTimer);
        } else {
          !this.analyzingProgress && this._simulateLoading();
        }
        this._cdRef.markForCheck();
      });
    this._store.select(fromStore.getWebsitesLoaded)
      .pipe(
        takeUntil(this._destroyStream$),
        filter((loaded: boolean) => !!loaded),
        switchMap(() => this._store.select(fromStore.getWebsite(this.websiteId))),
        first()
      )
      .subscribe((website: Website): void => {
        this.website = website;
        const scoreNew = this.website && this.website.score.currentScore && this.website.score.currentScore && this.website.score.currentScore.desktopScore ? this.website.score.currentScore.desktopScore : null;
        if (!TypeUtils.isNull(scoreNew) && scoreNew !== 101 && this.analyzing) {
          setTimeout(() => this.analyzing = false, 2000);
        }
        if (this.website.score && !this.website.domain_hash &&
          (
            TypeUtils.isNull(this.website.score)
            || (this.website.score && TypeUtils.isNull(this.website.score.originalScore))
            || (!TypeUtils.isNull(this.website.score.originalScore) && !TypeUtils.isUndefined(this.website.score.originalScore) && this.website.score.originalScore.desktopScore && this.website.score.originalScore.desktopScore === 101)
            || TypeUtils.isUndefined(this.website.score.originalScore)
            || (this.website.score && TypeUtils.isNull(this.website.score.currentScore))
            || (!TypeUtils.isNull(this.website.score.originalScore) && !TypeUtils.isUndefined(this.website.score.originalScore) && this.website.score.originalScore.desktopScore && this.website.score.currentScore.desktopScore === 101)
            || TypeUtils.isUndefined(this.website.score.currentScore)
          )
        ) {
          const scoreOld = this.website && this.website.score && this.website.score.originalScore && this.website.score.originalScore.desktopScore && this.website.score.originalScore.desktopScore !== 101 ? this.website.score.originalScore.desktopScore : null;
          if (TypeUtils.isNull(scoreOld)) {
            this.analyzingBefore = true;
          }
          this._getConnectionFlowStep();
        }

        if (TypeUtils.isNull(this.website.score) && !!this.website.domain_hash) {
          this.analyzing = true;
          this.analyzingProgress = 0;
          this._performanceService.getGooglePageSpeedScore(this.websiteId);
        }
      });

    this._store.select(fromStore.getWebsitePluginsLoaded(this.websiteId))
      .pipe(
        filter((loaded: boolean) => loaded),
        first(),
        switchMap(() => this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website?.id))),
        distinctUntilChanged((prev, curr) => prev && curr && prev.active === curr.active),
        takeUntil(this._destroyStream$)
      )
      .subscribe(boosterPlugin => {
        if (this.website && !!this.website.domain_hash && boosterPlugin && boosterPlugin.active) {
          this._redirect('booster/frontend');
        } else if (this.website && !!this.website.domain_hash && (!boosterPlugin || (boosterPlugin && !boosterPlugin.active))) {
          this._redirect('booster/disabled');
        }
        this.loading = false;
        this._cdRef.markForCheck();
      });
  }

  private _defineAnalyzing(activity): void {
    if (TypeUtils.isUndefined(activity)) {
      setTimeout(() => {
        this.analyzing = activity && activity.progress >= 0
          && activity.meta
          && (
            (!TypeUtils.isUndefined(activity.meta.is_first_optimization_flow) && (!!activity.meta.is_first_optimization_flow || TypeUtils.isNull(activity.meta.is_first_optimization_flow)))
            ||
            (!TypeUtils.isUndefined(activity.meta.check_with_no_optimization) && !activity.meta.check_with_no_optimization)
          );
      }, 1500);
    } else {
      this.analyzing = activity && activity.progress >= 0
        && activity.meta
        && (
          (!TypeUtils.isUndefined(activity.meta.is_first_optimization_flow) && (!!activity.meta.is_first_optimization_flow || TypeUtils.isNull(activity.meta.is_first_optimization_flow)))
          ||
          (!TypeUtils.isUndefined(activity.meta.check_with_no_optimization) && !activity.meta.check_with_no_optimization)
        );
    }
  }

  public recheckScore(): void {
    if (!this.boosterPluginIsActive) {
      this._showBoosterActivationPopup();
    } else {
      this.analyzing = true;
      this.analyzingProgress = 0;
      this._performanceService.getGooglePageSpeedScore(this.websiteId);
    }
  }

  private _showBoosterActivationPopup() {
    window.open(this.website.adminActivateBoosterUrl, "_blank", "noopener");
  }


  private _simulateLoading() {
    this.analyzingProgress = 0;
    this._simulationTimer = setInterval(() => {
      if (this.analyzingProgress === 60) {
        clearInterval(this._simulationTimer);
        this._simulationTimer = setInterval((): void => {
          if (this.analyzingProgress >= 98) {
            clearInterval(this._simulationTimer);
            return;
          } else {
            this.analyzingProgress++;
            this._cdRef.markForCheck();
          }
        }, 700);
      } else {
        this.analyzingProgress++;
        this._cdRef.markForCheck();
      }
    }, 50);
  }

  public enableBoosterPlugin(): void {
    if (this.optimizerPlugin && !this.optimizerPlugin.paidVersionInstalled) {
      this._installAndActivate();
    } else if (this.optimizerPlugin && !this.optimizerPlugin.active) {
      this._activate();
    }
  }

  private _installAndActivate(): void {
    this.loading = true;
    let network = 0;
    if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
      network = 1;
    }
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.optimizerPlugin],
      action: ActivityAction.INSTALL_ACTIVATE,
      network: network
    }));
  }

  private _activate(): void {
    this.loading = true;
    let network = 0;
    if (this.website.isNetwork || (!this.website.isNetwork && this.website.parentId)) {
      network = 1;
    }
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.optimizerPlugin],
      action: ActivityAction.ACTIVATE,
      network: network
    }));
  }

  private _redirect(url: string): void {
    this._router.navigate([`/websites/${this.websiteId}/${url}`]);
  }

  public sendEventWpSupport(): void {
    window.open('https://wordpress.org/support/plugin/tenweb-speed-optimizer/', '_blank', 'noopener,noreferrer,');
  }
}
